import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { saveAs } from 'file-saver'

import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
} from '@material-ui/core'
import { reverse } from 'named-urls'
import { useHistory } from 'react-router-dom'
import {
  EyeOff as EyeOffIcon,
  Eye as EyeIcon,
  Download as DownloadIcon,
} from 'react-feather'
import { useSnackbar } from 'hooks'

import { Card, LoadingFeedback, Select } from 'components'
import { MetadataInput } from './components'

import * as service from 'service'
import helpers from 'helpers'
import schema from './schema'
import useStyles from '../../styles'
import { routes } from 'Routes'
import constants from 'constants/index'

const SSOCorporateGroupForm = ({
  ssoCorporateGroup,
  edit = false,
  show = false,
  handlePlatformChange,
  refresh,
}) => {
  const [hasSCIM, setHasSCIM] = useState(ssoCorporateGroup?.scim || false)
  const [loading, setLoading] = useState(false)
  const [showScimToken, setShowScimToken] = useState(false)
  const [statusVerified, setStatusVerified] = useState(
    !!ssoCorporateGroup?.zitadelIdpActivated,
  )
  const [downloadingMetadata, setDownloadingMetadata] = useState(false)
  const [metadataXmlFile, setMetadataXmlFile] = useState(null)

  const snackbar = useSnackbar()
  const classes = useStyles()
  const history = useHistory()

  const handleChangeSwitch = (event) => setHasSCIM(event?.target?.checked)

  const handleToggleScimTokenVisibility = () => {
    setShowScimToken((prev) => !prev)
  }

  const { handleSubmit, errors, control, watch, setValue } = useForm({
    validationSchema: schema(hasSCIM),
    defaultValues: {
      samlProvider: ssoCorporateGroup?.samlProvider || 'zitadel',
      name: ssoCorporateGroup?.name || '',
      domain: ssoCorporateGroup?.domain || '',
      id: ssoCorporateGroup?.id || '',
      metadataInputType: 'url',
      metadataUrl: ssoCorporateGroup?.metadataUrl || '',
      metadataXml: ssoCorporateGroup?.metadataXml || '',
      assertionConsumerServiceUrl:
        ssoCorporateGroup?.assertionConsumerServiceUrl || '',
      serviceProviderEntityId: ssoCorporateGroup?.serviceProviderEntityId || '',
      identityProviderEntityId:
        ssoCorporateGroup?.identityProviderEntityId || '',
      redirectUrl: ssoCorporateGroup?.redirectUrl || '',
      scimToken: ssoCorporateGroup?.scimTokenDecrypted || '',
      platform: ssoCorporateGroup?.platform || 'dponet',
      idpProvider: ssoCorporateGroup?.idpProvider || 'entra_id',
    },
  })

  const isZitadelSamlProvider = watch('samlProvider') === 'zitadel'

  const onSubmit = async (data) => {
    try {
      setLoading(true)
      if (edit) {
        let response = undefined
        if (metadataXmlFile) {
          response = await service.dponet.ssoCorporateGroups.updateForm({
            ssoCorporateGroup: {
              ...data,
              metadataFile: metadataXmlFile,
              scim: hasSCIM,
            },
            ssoCorporateGroupId: ssoCorporateGroup?.id,
          })
        } else {
          response = await service.dponet.ssoCorporateGroups.update({
            ssoCorporateGroup: {
              ...data,
              scim: hasSCIM,
            },
            ssoCorporateGroupId: ssoCorporateGroup?.id,
          })
        }
        const ssoCorporateGroupUpdated = response?.data?.ssoCorporateGroup
        handlePlatformChange(ssoCorporateGroupUpdated.platform)
        setValue(
          'metadataUrl',
          ssoCorporateGroupUpdated.identityProviderEntityId ||
            ssoCorporateGroup?.metadataUrl ||
            '',
        )
        setValue(
          'identityProviderEntityId',
          ssoCorporateGroupUpdated.identityProviderEntityId ||
            ssoCorporateGroup?.identityProviderEntityId ||
            '',
        )
        setValue(
          'assertionConsumerServiceUrl',
          ssoCorporateGroupUpdated.assertionConsumerServiceUrl ||
            ssoCorporateGroup?.assertionConsumerServiceUrl ||
            '',
        )
        setValue(
          'serviceProviderEntityId',
          ssoCorporateGroupUpdated.serviceProviderEntityId ||
            ssoCorporateGroup?.serviceProviderEntityId ||
            '',
        )

        if (data.metadataInputType !== 'url') {
          setValue('metadataInputType', 'url')
        }

        setStatusVerified(
          ssoCorporateGroupUpdated.zitadelIdpActivated ||
            ssoCorporateGroup?.zitadelIdpActivated ||
            false,
        )
        return snackbar.open({
          message: 'Organização SSO-SAML editada com sucesso',
          variant: 'success',
        })
      }
      let response = undefined
      if (metadataXmlFile) {
        response = await service.dponet.ssoCorporateGroups.createForm({
          ssoCorporateGroup: {
            ...data,
            metadataFile: metadataXmlFile,
            scim: hasSCIM,
          },
        })
      } else {
        response = await service.dponet.ssoCorporateGroups.create({
          ssoCorporateGroup: {
            ...data,
            scim: hasSCIM,
          },
        })
      }

      snackbar.open({
        message: 'Organização SSO-SAML criada com sucesso',
        variant: 'success',
      })

      const ssoCorporateGroupId = response?.data?.ssoCorporateGroup?.id
      history.push(
        reverse(routes.ssoCorporateGroups.edit, { ssoCorporateGroupId }),
      )
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error || 'Erro ao criar organização SSO-SAML',
        ),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadmetadata = async () => {
    try {
      setDownloadingMetadata(true)
      const response = await service.dponet.ssoCorporateGroups.downloadMetadata(
        {
          ssoCorporateGroupId: ssoCorporateGroup?.id,
        },
      )
      const blob = new Blob([response?.data], {
        type: 'application/xml',
      })
      saveAs(blob, `${ssoCorporateGroup?.domain}-dponetsso-metadata.xml`)
    } catch {
      window.open(ssoCorporateGroup?.serviceProviderEntityId, '_blank')
    } finally {
      setDownloadingMetadata(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <LoadingFeedback open={loading} />
      <Card title="Preencha os dados da Organização SSO-SAML">
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Controller
              as={
                <Select
                  items={constants.ssoCorporateGroups.SAML_PROVIDERS}
                  error={!!errors.samlProvider}
                  helperText={errors?.samlProvider?.message}
                  label="Provedor SAML"
                  disabled={show}
                />
              }
              control={control}
              name="samlProvider"
              mode="onChange"
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="name"
              mode="onChange"
              as={
                <TextField
                  fullWidth
                  label="Nome"
                  error={!!errors.name}
                  helperText={errors?.name?.message}
                  disabled={show}
                />
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="domain"
              mode="onChange"
              as={
                <TextField
                  fullWidth
                  label="Domínio"
                  error={!!errors.domain}
                  helperText={errors?.domain?.message}
                  disabled={show}
                />
              }
            />
          </Grid>
          {(!isZitadelSamlProvider || edit) && (
            <>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="assertionConsumerServiceUrl"
                  mode="onChange"
                  as={
                    <TextField
                      fullWidth
                      label="ACS URL"
                      error={!!errors.assertionConsumerServiceUrl}
                      helperText={errors?.assertionConsumerServiceUrl?.message}
                      disabled={show || isZitadelSamlProvider}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="identityProviderEntityId"
                  mode="onChange"
                  as={
                    <TextField
                      fullWidth
                      label="ID Provedor de Identidade"
                      error={!!errors.identityProviderEntityId}
                      helperText={errors?.identityProviderEntityId?.message}
                      disabled={show || isZitadelSamlProvider}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="serviceProviderEntityId"
                  mode="onChange"
                  as={
                    <TextField
                      fullWidth
                      label="ID Provedor de Serviço"
                      error={!!errors.serviceProviderEntityId}
                      helperText={errors?.serviceProviderEntityId?.message}
                      disabled={show || isZitadelSamlProvider}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleDownloadmetadata}
                              edge="end"
                              disabled={
                                downloadingMetadata ||
                                !watch('serviceProviderEntityId')
                              }
                            >
                              <DownloadIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  }
                />
              </Grid>
            </>
          )}
          {isZitadelSamlProvider ? (
            <>
              <Grid item xs={12}>
                <Controller
                  as={
                    <Select
                      items={constants.ssoCorporateGroups.IDP_PROVIDERS}
                      error={!!errors.idpProvider}
                      helperText={errors?.idpProvider?.message}
                      label="Provedor de Identidade"
                      disabled={show}
                    />
                  }
                  control={control}
                  name="idpProvider"
                  mode="onChange"
                />
              </Grid>
              <MetadataInput
                control={control}
                watch={watch}
                errors={errors}
                disabled={show}
                setMetadataXmlFile={setMetadataXmlFile}
                ssoCorporateGroup={ssoCorporateGroup}
                statusVerified={statusVerified}
                setStatusVerified={setStatusVerified}
                refresh={refresh}
              />
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="redirectUrl"
                  mode="onChange"
                  as={
                    <TextField
                      fullWidth
                      label="URL de redirecionamento"
                      error={!!errors.redirectUrl}
                      helperText={errors?.redirectUrl?.message}
                      disabled={show}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  label="SCIM"
                  control={
                    <Switch
                      color="primary"
                      checked={hasSCIM}
                      onChange={handleChangeSwitch}
                      disabled={show}
                    />
                  }
                  name="scim"
                />
              </Grid>
              {hasSCIM && (
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="scimToken"
                    mode="onChange"
                    as={
                      <TextField
                        fullWidth
                        label="SCIM Token"
                        type={showScimToken ? 'text' : 'password'}
                        error={!!errors.scimToken}
                        helperText={errors?.scimToken?.message}
                        disabled={show}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleToggleScimTokenVisibility}
                                edge="end"
                              >
                                {showScimToken ? <EyeOffIcon /> : <EyeIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    }
                  />
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12}>
            <Controller
              as={
                <Select
                  items={constants.ssoCorporateGroups.PLATFORMS}
                  error={!!errors.platform}
                  helperText={errors?.platform?.message}
                  label="Plataforma"
                  disabled={show}
                />
              }
              control={control}
              name="platform"
              mode="onChange"
            />
          </Grid>
        </Grid>
        <Box className={classes.boxButtons}>
          {!show && (
            <Button variant="contained" color="primary" type="submit">
              Salvar
            </Button>
          )}
        </Box>
      </Card>
    </form>
  )
}

export default SSOCorporateGroupForm
